/**
 * ProPortalLayout
 * ================
 * Unified layout for all Professional Portal pages.
 * Provides consistent sidebar navigation, top header, and background styling.
 * 
 * Usage: Wrap /pro/* routes in router.tsx
 */

import { Outlet } from 'react-router-dom';
import { useMemo, useState } from 'react';
import ProPortalSidebar from '@/components/pro/ProPortalSidebar';
import ProTopHeader from '@/components/pro/ProTopHeader';
import { useProProfile } from '@/hooks/useProProfile';
import { useAuth } from '@/auth/AuthProvider';
import { resolveProStatus } from '@/domain/pro/resolveProStatus';
import LoadingSpinner from '@/components/LoadingSpinner';
import type { Professional } from '@/types/professional';

export default function ProPortalLayout() {
  const { user } = useAuth();
  const { proProfile, loading } = useProProfile();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  
  // Compute unified status resolution
  const resolution = useMemo(() => {
    if (!user || !proProfile) return null;
    return resolveProStatus(user, proProfile as unknown as Professional);
  }, [user, proProfile]);
  
  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-950 items-center justify-center">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-slate-400">Loading professional portal...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex min-h-screen flex-col bg-slate-950" data-testid="pro-portal-layout">
      {/* Top Header — spans the full width, above sidebar + content */}
      <ProTopHeader onMobileMenuToggle={() => setMobileNavOpen(v => !v)} />

      {/* Body row: sidebar + scrollable content */}
      <div className="flex flex-1 min-h-0">
        {/* Sidebar Navigation */}
        <ProPortalSidebar 
          professional={proProfile as unknown as Professional}
          resolution={resolution}
        />

        {/* Main Content Area */}
        <div className="relative flex flex-1 flex-col min-w-0 overflow-hidden">
          {/* Page Content */}
          <main className="relative z-10 flex-1 overflow-y-auto">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
